import { AppState } from 'reduxStore/appStore';
import { createSelector } from '@reduxjs/toolkit';
import { ActionPlanExistsQuery } from 'api/dto/new/ActionPlan/ActionPlanExists.dto';
import { LoadingStateEnum } from 'model/Resource';
import { PermissionType } from 'register/PermissionType';
import { selectCurrentProjectId, selectPulseTemplateId } from 'reduxStore/project/selectors';
import { selectIsAdmin, selectIsCircleAdmin, selectPermissions } from 'reduxStore/user/selectors';
import { selectSelectedDepartmentConnectedId } from 'reduxStore/departments/selectors';
import { OVERALL_DEPARTMENT_ID } from 'shared/constants/department';

import { ActionPlansState, STORE_NAME } from './initialState';

export const selectActionPlansDomain = (state: AppState): ActionPlansState.Domain =>
  state[STORE_NAME];

// The query identifying "the" action plan for the current client + pulse + department
// selection — one plan per department per pulse. `null` for regular (non-pulse-template)
// projects, which have no client id to scope a plan by.
export const selectActionPlanExistsQuery = createSelector(
  selectPulseTemplateId,
  selectCurrentProjectId,
  selectSelectedDepartmentConnectedId,
  (pulseTemplateId, projectId, connectedId): ActionPlanExistsQuery | null =>
    pulseTemplateId && projectId
      ? {
          externalClientId: pulseTemplateId,
          externalPulseId: projectId,
          externalDepartmentId: connectedId ?? OVERALL_DEPARTMENT_ID,
        }
      : null
);

export const selectActionPlanExistsResource = createSelector(
  selectActionPlansDomain,
  (domain) => domain.exists
);

export const selectActionPlanExists = createSelector(
  selectActionPlanExistsResource,
  (resource) => resource.data?.exists ?? false
);

export const selectActionPlanId = createSelector(
  selectActionPlanExistsResource,
  (resource) => resource.data?.actionPlanId ?? null
);

export const selectIsActionPlanExistsLoading = createSelector(
  selectActionPlanExistsResource,
  (resource) => resource.loadingState === LoadingStateEnum.PENDING
);

export const selectHasActionPlanExistsError = createSelector(
  selectActionPlanExistsResource,
  (resource) => resource.loadingState === LoadingStateEnum.REJECTED
);

export const selectHasActCreatePermission = createSelector(
  [selectPermissions, selectIsAdmin, selectIsCircleAdmin],
  (permissions = [], isAdmin, isCircleAdmin) =>
    isAdmin || isCircleAdmin || permissions.includes(PermissionType.ActCreate)
);

export const selectHasActReportingPermission = createSelector(
  [selectPermissions, selectIsAdmin, selectIsCircleAdmin],
  (permissions = [], isAdmin, isCircleAdmin) =>
    isAdmin || isCircleAdmin || permissions.includes(PermissionType.ActReporting)
);
